// Server sessions whose unsent audio an offline stop handed to the offline queue.
//
// A capture that started online and lost the network keeps its server session.
// At the stop, Rust queues the audio it could not upload as a take on a local
// offline consult bound to that server session, and reports the consult's local
// id. The offline settings write is keyed by that local id, while the session
// on screen is keyed by its server id, so this map is how the template picker
// finds the row to write to.
//
// A bridge, not a store: an entry only answers until the offline queue lists
// the consult (`acknowledgeListedHandovers`), after which the queue answers,
// and it is dropped too when the consult syncs (`forgetOfflineHandover`). So an
// entry lives for the moment between the stop and the next queue read, which
// is why nothing has to clear it at sign-out.

import { atom, getDefaultStore } from 'jotai';

import {
  OFFLINE_FAILED_STATUSES,
  OFFLINE_SYNC_IN_FLIGHT_STATUSES,
  type OfflineSyncItem,
  type OfflineSyncStatus,
} from '@/types/offline-recording';

/** `serverSessionId -> localSessionId`, written by the stop that handed over. */
export const offlineHandoverLocalIdBySessionIdAtom = atom<Record<string, string>>({});

export function recordOfflineHandover(sessionId: string, localSessionId: string): void {
  getDefaultStore().set(offlineHandoverLocalIdBySessionIdAtom, (current) => ({
    ...current,
    [sessionId]: localSessionId,
  }));
}

/**
 * Drop the entry for a consult that has synced. Once Rust has removed the
 * row, a write to its id fails, so the session must stop routing to it. Called
 * from the `offline_recording:synced` handler, for a consult that synced before
 * any queue read listed it.
 */
export function forgetOfflineHandover(localSessionId: string): void {
  dropWhere((local) => local === localSessionId);
}

function dropWhere(drop: (localSessionId: string) => boolean): void {
  getDefaultStore().set(offlineHandoverLocalIdBySessionIdAtom, (current) => {
    const kept = Object.entries(current).filter(([, local]) => !drop(local));
    return kept.length === Object.keys(current).length
      ? current
      : Object.fromEntries(kept);
  });
}

/**
 * Hand every listed consult over to the queue. Called with each queue read: a
 * listed consult is resolved from the queue from then on, which knows when it
 * has synced; the map does not.
 */
export function acknowledgeListedHandovers(
  listedLocalSessionIds: readonly string[]
): void {
  const listed = new Set(listedLocalSessionIds);
  dropWhere((local) => listed.has(local));
}

/** Drop every entry. For tests. */
export const clearOfflineHandovers = (): void => {
  getDefaultStore().set(offlineHandoverLocalIdBySessionIdAtom, {});
};

/**
 * Statuses in which the consult holds its take and its settings will be read
 * by a sync run that has not started yet: queued, or failed and due a retry.
 * In-flight statuses (`SYNCING`) are left out on purpose: a run reads the
 * settings once, at its start, so a write during one is lost even if the link
 * drops mid-run. `synced` and
 * `unrecoverable` are done with, and `recording` is a capture still running
 * into the session, not a handover.
 */
const AWAITING_SYNC = new Set<OfflineSyncStatus>([
  'pending_sync',
  // Failed and due a retry: the next run re-reads the settings. `unrecoverable`
  // never runs again.
  ...OFFLINE_FAILED_STATUSES.filter((status) => status !== 'unrecoverable'),
]);

/**
 * A sync run is working on the consult. It re-reads the settings after chunk
 * prep, at a point the app cannot observe.
 */
const SYNCING = new Set<OfflineSyncStatus>(OFFLINE_SYNC_IN_FLIGHT_STATUSES);

/**
 * The local consult holding this server session's handed-over audio, until it
 * has synced; otherwise `undefined`.
 *
 * - `syncing: false` — no sync run has read the consult yet (`AWAITING_SYNC`),
 *   so a template saved to it now is the one the note is generated with.
 * - `syncing: true` — a run has started. It re-reads the settings after chunk
 *   prep, which the app cannot observe, so a save may or may not land; and
 *   generating from the server now would race the run's own note with only the
 *   pre-drop audio. A caller does neither.
 *
 * The queue decides whenever it lists a consult bound to the session. The map
 * answers only before the first queue read that lists the consult. Connectivity
 * is deliberately not a condition: the picker can sit open across a reconnect.
 */
export function resolveHandoverConsult(
  sessionId: string | null | undefined,
  handovers: Record<string, string>,
  queue: OfflineSyncItem[]
): { localSessionId: string; syncing: boolean } | undefined {
  if (!sessionId) {
    return undefined;
  }
  const recorded = handovers[sessionId];
  // More than one consult can be bound to a session (an earlier take already
  // synced, say), so a waiting or syncing one wins over a finished one.
  let syncingId: string | undefined;
  let boundAny = false;
  for (const item of queue) {
    if (
      item.serverSessionId !== sessionId ||
      item.localSessionId === sessionId ||
      (recorded !== undefined && item.localSessionId !== recorded)
    ) {
      continue;
    }
    if (AWAITING_SYNC.has(item.status)) {
      return { localSessionId: item.localSessionId, syncing: false };
    }
    if (SYNCING.has(item.status)) {
      syncingId ??= item.localSessionId;
    }
    boundAny = true;
  }
  if (syncingId) {
    return { localSessionId: syncingId, syncing: true };
  }
  if (boundAny || recorded === undefined) {
    return undefined;
  }
  return { localSessionId: recorded, syncing: false };
}
